import type { StudentAction } from '../types'
import { ContactParentDialog } from './contact-parent'
import { DeleteChildAccountDialog } from './delete-child-account'
import { GivePremiumDialog } from './give-premium'
import { RemoveStudentDialog } from './remove-student'
import { RequestHomeConnectDialog } from './request-home-connect'
import { StudentSettingsDialog } from './student-settings'

interface StudentDialogsProps {
  action: StudentAction | null
  /** Drives copy in the dialogs that differ between a school and a family. */
  type?: 'web' | 'school'
  /**
   * Lets a dialog replace itself with another. `RemoveStudentDialog`'s "Sambungkan dulu" is the
   * only user today: swapping the single held action keeps exactly one dialog mounted, where
   * nesting a second `Dialog` inside the first would fight over focus and the backdrop.
   */
  onAction: (action: StudentAction) => void
  onClose: () => void
}

/**
 * Single dialog host for the table's row actions, whether they come from the kebab menu or from a
 * cell's own control. The table holds at most one `StudentAction` at a time and mounts exactly one
 * dialog for it here, rather than mounting every dialog per row.
 */
export function StudentDialogs({ action, type, onAction, onClose }: StudentDialogsProps) {
  if (!action) return null

  switch (action.kind) {
    case 'settings':
      return <StudentSettingsDialog student={action.student} type={type} onClose={onClose} />
    case 'give-premium':
      return <GivePremiumDialog student={action.student} onClose={onClose} />
    case 'remove':
      // The family arm is reached only by apps/web's own column today (the row menu that fires
      // `{ kind: 'remove' }` for `type === 'web'` lives in row-actions.tsx, unmounted in
      // apps/school). It exists so the school-only `RemoveStudentDialog` can never be shown to a
      // parent, even once that column is wired up.
      return type === 'web' ? (
        <DeleteChildAccountDialog student={action.student} onClose={onClose} />
      ) : (
        <RemoveStudentDialog student={action.student} onAction={onAction} onClose={onClose} />
      )
    case 'request-home-connect':
      return <RequestHomeConnectDialog student={action.student} onClose={onClose} />
    case 'contact-parent':
      return <ContactParentDialog student={action.student} onClose={onClose} />
  }
}

export {
  ContactParentDialog,
  DeleteChildAccountDialog,
  GivePremiumDialog,
  RemoveStudentDialog,
  RequestHomeConnectDialog,
  StudentSettingsDialog,
}
